import { useState, type CSSProperties } from "react";
import { formatUSD, DAY_MS, type TrendPoint } from "../../analytics";
import { MetricHint } from "./KpiCards";
import { SAVED_OVER_TIME_HINT } from "./metricHints";

const HOUR_MS = 3_600_000;

/* Saved-over-time — the Figma overview's SavedChart (node 284:571): a 15px
 * "Saved over the …" title, the big light total, and a strip of thin
 * candle-style bars (dark saved cap over a grey net-paid stem) crossed by a
 * dashed average line, with mono bucket labels underneath.
 *
 * Each bar's total height is the pre-pipeline gross (net paid + savings); the
 * dark cap on top is what the gateway saved, the grey stem below is what was
 * actually paid. The /trends API reports gross cost per bucket only, so the
 * window's savings are allocated to each bucket in proportion to its cost
 * (savedRatio). */
export function TrendChart({
  points,
  savedRatio,
  caption,
  from,
  to,
  granularity,
}: {
  points: TrendPoint[];
  savedRatio: number;
  caption: string;
  /** Window bounds (ISO) — the axis spans the whole selected range. */
  from: string;
  to: string;
  granularity: "hour" | "day";
}) {
  const [hover, setHover] = useState<number | null>(null);

  // One column per bucket across the full window (30 for 30 days, 24 for
  // 24 hours, …): the API only returns buckets with traffic, so quiet
  // buckets are zero-filled here to keep the time axis honest.
  const stepMs = granularity === "hour" ? HOUR_MS : DAY_MS;
  const floor = (ms: number) => ms - (ms % stepMs); // UTC-aligned buckets
  const spendByBucket = new Map(
    points.map((p) => [floor(Date.parse(p.bucket)), p.cost.total]),
  );
  const data: Array<{
    spend: number;
    savings: number;
    baseline: number;
    label: string;
  }> = [];
  for (let t = floor(Date.parse(from)); t < Date.parse(to); t += stepMs) {
    const spend = spendByBucket.get(t) ?? 0;
    const savings = spend * savedRatio;
    const label =
      granularity === "hour"
        ? `${String(new Date(t).getUTCHours()).padStart(2, "0")}:00`
        : new Date(t).toLocaleDateString("en-US", {
            timeZone: "UTC",
            month: "short",
            day: "numeric",
          });
    data.push({ spend, savings, baseline: spend + savings, label });
  }

  // With many columns (24h / 30d) the labels would collide — thin them to
  // roughly ten, keeping every column's slot so they stay centered.
  const labelEvery = Math.ceil(data.length / 10);

  const MAX = Math.max(1, ...data.map((d) => d.baseline)) * 1.07;
  const totalSaved = data.reduce((s, d) => s + d.savings, 0);

  /* Bar heights are square-rooted, not linear: one spike bucket is routinely
   * several times a normal one, and linearly that flattens every other bar onto
   * the floor — the shape people read the chart for. sqrt keeps zero at zero and
   * the ordering intact while giving ordinary buckets visible height; exact
   * figures stay in the hover tooltip. The average line uses the same scale. */
  const frac = (v: number) => (v > 0 ? Math.sqrt(v / MAX) : 0);
  const avgFrac =
    data.length > 0
      ? frac(data.reduce((s, d) => s + d.baseline, 0) / data.length)
      : 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1.5">
        <p
          className="cursor-default text-[15px] leading-none"
          style={{ color: "var(--text)" }}
        >
          Saved{" "}
          <span style={{ color: "var(--text-muted)" }}>over the {caption}</span>
        </p>
        <MetricHint label="Saved over time" hint={SAVED_OVER_TIME_HINT} />
      </div>
      <div
        className="mt-3 text-[40px] font-light leading-none tracking-tight tabular-nums"
        style={{ color: "var(--text)" }}
      >
        {formatUSD(totalSaved)}
      </div>

      {/* Bars over the dashed window-average line; hover for the breakdown */}
      <div className="relative mt-auto h-[130px]">
        <div
          className="absolute inset-x-0 border-t border-dashed"
          style={{
            borderColor: "var(--text-muted)",
            bottom: `${avgFrac * 100}%`,
          }}
        />
        <div className="relative flex h-full items-end">
          {data.map((d, i) => {
            const barH = frac(d.baseline) * 100;
            const savedFrac = d.baseline > 0 ? d.savings / d.baseline : 0;
            const isActive = hover === i;
            return (
              <div
                key={i}
                className="relative flex h-full flex-1 cursor-default items-end justify-center"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div
                  className="tw-bar-up flex w-[5px] flex-col overflow-hidden rounded-full"
                  style={
                    {
                      height: `${barH}%`,
                      minHeight: "5px",
                      // Bars grow up in sequence once the card has floated in.
                      "--bar-delay": `${0.4 + (i / data.length) * 0.4}s`,
                    } as CSSProperties
                  }
                >
                  <div
                    style={{
                      height: `${savedFrac * 100}%`,
                      background: isActive
                        ? "var(--val-crimson)"
                        : "var(--text)",
                      transition: "background 120ms ease",
                    }}
                  />
                  <div style={{ flex: 1, background: "var(--border)" }} />
                </div>
                {isActive && (
                  <div
                    className="absolute bottom-full left-1/2 z-10 mb-2.5 -translate-x-1/2 whitespace-nowrap rounded-[5px] px-3 py-2 shadow-lg"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border-2)",
                    }}
                  >
                    <div className="mb-1.5 text-[10px] font-semibold text-[var(--text)]">
                      {d.label} · {formatUSD(d.savings)} saved
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
                      <span
                        className="h-2 w-2 flex-shrink-0"
                        style={{ background: "var(--text)" }}
                      />{" "}
                      Saved
                      <span className="ml-auto pl-4 font-medium tabular-nums text-[var(--text)]">
                        {formatUSD(d.savings)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">
                      <span
                        className="h-2 w-2 flex-shrink-0"
                        style={{ background: "var(--border)" }}
                      />{" "}
                      Net paid
                      <span className="ml-auto pl-4 font-medium tabular-nums text-[var(--text)]">
                        {formatUSD(d.spend)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Period labels */}
      <div className="mt-3 flex">
        {data.map((d, i) => (
          <span
            key={i}
            className="tw-mono min-w-0 flex-1 whitespace-nowrap text-center text-[9px]"
            style={{ color: "var(--text)" }}
          >
            {i % labelEvery === 0 ? d.label : ""}
          </span>
        ))}
      </div>
    </div>
  );
}
